import React, { useEffect } from 'react'
import { Form, Input, Modal, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  ProductActions,
  getIsMutatingPermission,
  getModalVisibility,
  getPermissionGroups,
  getPermissionSelected,
  ModalName,
} from '~/Store/Products'
import { IProduct } from '~/Services/Product.interface'

const { Option } = Select

interface Props {
  product: IProduct
}

// Same Add/Edit modal convention as ProductFormModal.tsx / ApplicationFormModal.tsx — undefined
// permissionSelected means create mode, populated means edit mode.
export const PermissionFormModal = ({ product }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const visible = useAppSelector(getModalVisibility(ModalName.Permission))
  const confirmLoading = useAppSelector(getIsMutatingPermission)
  const permission = useAppSelector(getPermissionSelected)
  const permissionGroups = useAppSelector(getPermissionGroups)
  const [form] = Form.useForm()

  const isEdit = !!permission

  useEffect(() => {
    if (permission) {
      form.setFieldsValue({
        name: permission.claimValue,
        displayName: permission.displayName,
        groupName: permission.permissionGroup?.name,
        parentName: permission.parentClaimValue,
      })
    } else {
      form.resetFields()
    }
  }, [permission])

  const close = () => {
    dispatch(ProductActions.setPermissionSelected(undefined))
    dispatch(
      ProductActions.setModalVisibility({
        name: ModalName.Permission,
        visible: false,
      }),
    )
  }

  const onSubmit = () => {
    form.validateFields().then(values => {
      const data = {
        name: values.name.trim(),
        groupName: values.groupName,
        displayName: values.displayName?.trim() || undefined,
        parentName: values.parentName?.trim() || undefined,
      }
      if (isEdit && permission) {
        dispatch(
          ProductActions.updatePermission({
            productId: product.guid,
            permissionId: permission.guid,
            data,
          }),
        )
      } else {
        dispatch(ProductActions.createPermission({ productId: product.guid, data }))
      }
    })
  }

  return (
    <Modal
      visible={visible}
      title={isEdit ? t('admin.editPermission') : t('admin.addPermission')}
      confirmLoading={confirmLoading}
      onCancel={close}
      onOk={onSubmit}
      okText={t('save')}
      cancelText={t('cancel')}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          label={t('admin.permissionName')}
          name="name"
          rules={[
            { required: true, whitespace: true, message: t('fieldRequired') },
          ]}
        >
          <Input placeholder="user.view" />
        </Form.Item>
        <Form.Item label={t('admin.displayName')} name="displayName">
          <Input />
        </Form.Item>
        <Form.Item
          label={t('admin.group')}
          name="groupName"
          rules={[{ required: true, message: t('fieldRequired') }]}
        >
          <Select placeholder={t('admin.selectGroup')} showSearch optionFilterProp="children">
            {permissionGroups.map(g => (
              <Option key={g.guid} value={g.name}>
                {g.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {/* Free text, not a Select — matches the Excel import's own ParentName column (plain string,
            not a FK); backend validates it resolves to a real permission when provided. */}
        <Form.Item
          label={t('admin.parentPermission')}
          name="parentName"
          extra={t('admin.parentPermissionHint')}
        >
          <Input placeholder="user" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
